import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnDestroy } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { Subscription } from 'rxjs';
import { Product } from '../../core/models/product.model';
import { ProductService } from '../../core/services/product.service';
import {
  DEFAULT_SITE_CONTENT,
  ProductsPageContent,
  SiteContentService,
} from '../../core/services/site-content.service';

interface PublicProduct {
  product: Product;
  children: Product[];
}

@Component({
  selector: 'app-products-page',
  standalone: true,
  imports: [CommonModule, FormsModule, RouterLink],
  templateUrl: './products-page.component.html',
  styleUrls: ['./products-page.component.scss'],
})
export class ProductsPageComponent implements OnDestroy {
  productGroups: PublicProduct[] = [];
  searchTerm = '';
  selectedCategory = '';
  isLoading = true;
  errorMessage = '';
  pageContent: ProductsPageContent = DEFAULT_SITE_CONTENT.productsPage;
  private readonly productsSubscription: Subscription;
  private readonly loadingSubscription: Subscription;
  private readonly errorSubscription: Subscription;
  private readonly contentSubscription: Subscription;

  constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly productService: ProductService,
    private readonly siteContentService: SiteContentService,
  ) {
    this.contentSubscription = this.siteContentService
      .getSection<ProductsPageContent>('productsPage')
      .subscribe((content) => {
        this.pageContent = { ...DEFAULT_SITE_CONTENT.productsPage, ...(content || {}) };
        this.changeDetectorRef.detectChanges();
      });
    this.productsSubscription = this.productService.getActiveParentProducts().subscribe((products) => {
      const activeParents = products
        .filter((product) => product.type === 'parent' && product.status === 'active')
        .sort((a, b) => a.displayOrder - b.displayOrder || a.name.localeCompare(b.name));

      this.productGroups = activeParents.map((product) => ({
        product,
        children: [],
      }));
      this.changeDetectorRef.detectChanges();
    });
    this.loadingSubscription = this.productService.loading$.subscribe((isLoading) => {
      this.isLoading = isLoading;
      this.changeDetectorRef.detectChanges();
    });
    this.errorSubscription = this.productService.error$.subscribe((message) => {
      this.errorMessage = message ?? '';
      this.changeDetectorRef.detectChanges();
    });
  }

  get categories(): string[] {
    return Array.from(new Set(this.productGroups.map((group) => group.product.category).filter(Boolean))).sort();
  }

  get filteredProductGroups(): PublicProduct[] {
    const search = this.searchTerm.trim().toLowerCase();

    return this.productGroups.filter((group) => {
      const matchesCategory = !this.selectedCategory || group.product.category === this.selectedCategory;
      const searchableText = [
        group.product.name,
        group.product.category,
        group.product.shortDescription,
        group.product.longDescription,
        ...group.children.map((child) => child.name),
      ]
        .join(' ')
        .toLowerCase();

      return matchesCategory && (!search || searchableText.includes(search));
    });
  }

  ngOnDestroy(): void {
    this.productsSubscription.unsubscribe();
    this.loadingSubscription.unsubscribe();
    this.errorSubscription.unsubscribe();
    this.contentSubscription.unsubscribe();
  }

  trackByProduct(_: number, group: PublicProduct): string {
    return group.product.id;
  }

  trackByChild(_: number, product: Product): string {
    return product.id;
  }
}
